import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PhotoUploadModal, { TImage } from '../Feed/PhotoUploadModal';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';

type PictureUploaderProps = {
    initialImageSrc?: string | TImage[];
    onChange: (image: string | TImage[] | null, rawFile?: File[] | FileList | null, altenateKey?: string) => void;
    onRemove: () => void;
    sx?: object;
    multiple?: boolean;
    aspect?: number; // Custom crop ratio
    isEditMode?: boolean;
    maxCount?: number;
    supportedTypes?: ('image' | 'video')[];
};

const PictureUploader: React.FC<PictureUploaderProps> = ({
    initialImageSrc,
    onChange,
    onRemove,
    sx = {},
    multiple,
    aspect,
    isEditMode = true,
    maxCount = 1,
    supportedTypes,
}) => {
    const [imageSrc, setImageSrc] = useState<string | TImage[] | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { themeStyle } = useThemeContext();

    // Cropper modal state
    const [cropperOpen, setCropperOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [, setPendingFile] = useState<File | null>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            // Open cropper modal for single image mode
            if (!multiple) {
                setSrc(URL.createObjectURL(file));
                setPendingFile(file);
                setCropperOpen(true);
            } else {
                // For multiple, keep old behavior
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    setImageSrc(result);
                    onChange(result, [file], 'singleRawFile');
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleUpload = () => {
        if (!multiple && fileInputRef.current) {
            fileInputRef.current.click();
        } else {
            onChange(imageSrc);
        }
    };

    const handleRemove = () => {
        setImageSrc(null);
        onRemove();
        onChange(null); // Notify parent component that the image has been removed
    };

    // Compress image utility
    const compressImage = (base64: string, quality = 0.7): Promise<string> => {
        return new Promise(resolve => {
            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0);
                canvas.toBlob(
                    blob => {
                        if (blob) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                resolve(reader.result as string);
                            };
                            reader.readAsDataURL(blob);
                        } else {
                            resolve(base64);
                        }
                    },
                    'image/jpeg',
                    quality,
                );
            };
            img.src = base64;
        });
    };

    // Handle cropper save
    const handleCropSave = async (base64: string, file: File) => {
        setCropperOpen(false);
        setSrc('');
        setPendingFile(null);
        // Compress the cropped image
        const compressed = await compressImage(base64, 0.7);
        setImageSrc(compressed);
        onChange(compressed, [file], 'singleRawFile');
    };

    const handleCropCancel = () => {
        setCropperOpen(false);
        setSrc('');
        setPendingFile(null);
    };

    useEffect(() => {
        if (initialImageSrc) {
            setImageSrc(initialImageSrc);
        }
    }, [initialImageSrc]);

    if (multiple) {
        return (
            <PhotoUploadModal
                onCancelMediaUpload={() => {
                    onChange(null, null);
                    handleRemove();
                }}
                initialValue={initialImageSrc as TImage[]}
                onChange={(item, rawFiles) => {
                    onChange(item, rawFiles);
                }}
                maxCount={maxCount}
                supportedTypes={supportedTypes}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                ...sx,
            }}>
            {/* Hidden file input for single image upload */}
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            {/* Picture Upload Box */}
            <Box
                sx={{
                    position: 'relative',
                    width: 200,
                    height: 200,
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: imageSrc ? 'transparent' : theme[themeStyle].secondaryBg,
                    overflow: 'hidden',
                }}
                onClick={handleUpload}>
                {imageSrc ? (
                    <img
                        src={imageSrc as string}
                        alt="Uploaded"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                )}

                {imageSrc && isEditMode && (
                    <IconButton
                        onClick={e => {
                            e.stopPropagation();
                            handleRemove();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'white',
                            boxShadow: 1,
                            '&:hover': { backgroundColor: 'grey.200' },
                        }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {/* Cropper Modal for single image mode */}
            <RectangularImageCropperModal
                open={cropperOpen}
                src={src}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={aspect || 1}
            />
        </Box>
    );
};

export default PictureUploader;
